import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alerts } from 'icons/untitledui';
import { getUrgencyColors } from 'dashboard/urgencyColors';
import {
  SelectionDropdown,
  type SelectionDropdownOption,
} from 'shared/components/dropdown/SelectionDropdown';
import { useOverviewNumbers } from 'dashboard/hooks/useOverviewNumbers';
import { useOverviewUrgency } from 'dashboard/hooks/useOverviewUrgency';
import { useOverviewRisks } from 'dashboard/hooks/useOverviewRisks';
import { useInsightsEvents } from 'dashboard/hooks/useInsightsEvents';
import colours from 'theme/atoms/colours';

import * as Styled from 'dashboard/styles/Compare.style';

interface CompareColumnProps {
  queryParams: string;
  departmentOptions: SelectionDropdownOption[];
  selectedDepartmentId: string;
  onDepartmentChange: (departmentId: string) => void;
  showDetails: boolean;
}

export default function CompareColumn({
  queryParams,
  departmentOptions,
  selectedDepartmentId,
  onDepartmentChange,
  showDetails,
}: CompareColumnProps): JSX.Element {
  const { t } = useTranslation('pageDashboard');

  const departmentQueryParams = useMemo(() => {
    const params = new URLSearchParams(queryParams);
    params.set('department_ids', selectedDepartmentId);
    return params.toString();
  }, [queryParams, selectedDepartmentId]);

  const { data: numbers, isLoading: numbersLoading } = useOverviewNumbers(
    departmentQueryParams
  );
  const { data: urgency } = useOverviewUrgency(departmentQueryParams);
  const { data: risks } = useOverviewRisks(departmentQueryParams);
  const { data: events, isLoading: eventsLoading } = useInsightsEvents(
    departmentQueryParams
  );

  const overviewRows = [
    {
      key: 'signals',
      label: t('overview.signalsCaptured'),
      value: numbers?.notes_count,
      isLoading: numbersLoading,
    },
    {
      key: 'events',
      label: t('overview.events'),
      value: events?.pages[0]?.count,
      isLoading: eventsLoading,
    },
    {
      key: 'incidents',
      label: t('overview.incidents'),
      value: numbers?.incidents_count,
      isLoading: numbersLoading,
    },
  ];

  return (
    <Styled.Column>
      <SelectionDropdown
        ariaLabel={t('compare.selectDepartment')}
        value={selectedDepartmentId}
        options={departmentOptions}
        onChange={onDepartmentChange}
        fullWidth
      />

      <Styled.Group>
        <Styled.GroupTitle>{t('overview.title')}</Styled.GroupTitle>
        {overviewRows.map((row) => (
          <Styled.Row key={row.key}>
            <Styled.RowLabel>
              <Styled.RowLabelText>{row.label}</Styled.RowLabelText>
            </Styled.RowLabel>
            <Styled.RowValues>
              <Styled.RowValue>
                {row.isLoading ? '–' : row.value ?? 0}
              </Styled.RowValue>
            </Styled.RowValues>
          </Styled.Row>
        ))}
      </Styled.Group>

      {showDetails && (
        <>
          <Styled.Group>
            <Styled.GroupTitle>{t('compare.severity')}</Styled.GroupTitle>
            {urgency?.map((item) => (
              <Styled.Row key={item.category_key}>
                <Styled.RowLabel>
                  <Styled.RowLabelIcon
                    $color={
                      getUrgencyColors(item.category_key)?.text ??
                      colours.neutral[500]
                    }
                    aria-hidden="true"
                  >
                    <Alerts.AlertCircle />
                  </Styled.RowLabelIcon>
                  <Styled.RowLabelText>{item.category}</Styled.RowLabelText>
                </Styled.RowLabel>
                <Styled.RowValues>
                  <Styled.RowCount>{item.count}</Styled.RowCount>
                  <Styled.RowValue>{item.percentage}%</Styled.RowValue>
                </Styled.RowValues>
              </Styled.Row>
            ))}
          </Styled.Group>

          <Styled.Group>
            <Styled.GroupTitle>{t('compare.topRisks')}</Styled.GroupTitle>
            {risks?.map((item) => (
              <Styled.Row key={item.risk_key}>
                <Styled.RowLabel>
                  <Styled.RowLabelText>{item.risk}</Styled.RowLabelText>
                </Styled.RowLabel>
                <Styled.RowValues>
                  <Styled.RowCount>{item.note_count}</Styled.RowCount>
                  <Styled.RowValue>{item.percentage}%</Styled.RowValue>
                </Styled.RowValues>
              </Styled.Row>
            ))}
          </Styled.Group>
        </>
      )}
    </Styled.Column>
  );
}
